import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useVerifyEmailMutation } from '@/shared/api/endpoints';
import { errorCode } from '@/shared/api/errors';
import { homeUrl, interfaceOf } from '@/shared/lib/roles';
import { browser, isElsewhere } from '@/shared/lib/site';
import { Button } from '@/shared/ui/Button';
import { Alert, TextField } from '@/shared/ui/primitives';

import { useHandle } from './useHandle';

export const MIN_PASSWORD = 8;

interface SetPasswordFormProps {
  token: string;
  email: string;
  /** A free handle the server made from the display name: what the account gets
   * unless another is typed. */
  handle: string;
}

/**
 * The last step of registering, on the page the mailed link leads to: the password
 * and the name in addresses are chosen here, by whoever could open the mail, and by
 * nobody before that. Saving them confirms the address and signs in.
 */
export function SetPasswordForm({ token, email, handle: suggested }: SetPasswordFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [verify, state] = useVerifyEmailMutation();
  const handle = useHandle(suggested);
  const [password, setPassword] = useState('');
  const [repeated, setRepeated] = useState('');
  // Typed twice: there is no way to recover a password that was mistyped once.
  const [problem, setProblem] = useState<'short' | 'mismatch' | null>(null);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    // The browser checks the length too; this does not depend on it.
    const found = password.length < MIN_PASSWORD ? 'short' : password !== repeated ? 'mismatch' : null;
    setProblem(found);
    if (found) return;
    const confirmed = await verify({
      token,
      password,
      ...(handle.chosen ? { handle: handle.chosen } : {}),
    });
    const user = confirmed.data?.session?.user;
    if (!user) return;
    // Signed in: on to where this role works, which may be another host.
    const to = homeUrl(interfaceOf(user.role));
    if (isElsewhere(to)) browser.leave(to);
    else await navigate(to, { replace: true });
  };

  const code = state.error ? (errorCode(state.error) ?? 'unknown') : null;
  return (
    <form className="space-y-4" noValidate onSubmit={(event) => void onSubmit(event)}>
      <p>{t('auth.verify.password.intro')}</p>
      {/* Read-only, and here for password managers too: what the password belongs to. */}
      <TextField label={t('auth.email')} type="email" value={email} autoComplete="username" readOnly />
      <TextField
        label={t('auth.handle.label')}
        value={handle.value}
        onChange={(event) => handle.type(event.target.value.trim())}
        autoComplete="off"
        autoCapitalize="none"
        spellCheck={false}
        maxLength={32}
        // Only what is known to be unusable: a name being checked is not an error yet.
        aria-invalid={handle.refused}
        hint={
          handle.status === 'suggested' || handle.status === 'available'
            ? t('auth.handle.preview', { origin: window.location.host, handle: handle.value })
            : t(`auth.handle.${handle.status}`)
        }
      />
      <TextField
        label={t('auth.password')}
        type="password"
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        autoComplete="new-password"
        minLength={MIN_PASSWORD}
        maxLength={128}
        hint={t('auth.passwordHint', { count: MIN_PASSWORD })}
        required
      />
      <TextField
        label={t('auth.verify.password.repeat')}
        type="password"
        value={repeated}
        onChange={(event) => setRepeated(event.target.value)}
        autoComplete="new-password"
        maxLength={128}
        required
      />
      {problem ? (
        <Alert tone="bad" live>
          {problem === 'short'
            ? t('auth.passwordHint', { count: MIN_PASSWORD })
            : t('auth.verify.password.mismatch')}
        </Alert>
      ) : null}
      {code && !problem ? (
        <Alert tone="bad" live>
          {code === 'invalid_verification_token'
            ? t('auth.verify.failed')
            : t(`errors.${code}`, { defaultValue: t('errors.unknown') })}
        </Alert>
      ) : null}
      <Button
        type="submit"
        size="lg"
        className="w-full"
        disabled={state.isLoading || !handle.ok}
      >
        {t('auth.verify.password.submit')}
      </Button>
    </form>
  );
}
